/**
 * Wires the relay channel, the crypto engine and the local store together.
 *
 * Inbound:  deliver -> decrypt -> persist -> ack -> (UI updates via Dexie live query)
 * Outbound: persist(queued) -> encrypt -> channel.send -> on `sent` mark sent;
 *           delivery/read receipts arrive as encrypted `receipt` envelopes.
 */
import type { DeliverFrame, EnvelopeKind } from "@truewave/protocol";
import { DELIVERED_TTL_MS } from "@truewave/protocol";
import type { CallSignalPayload } from "../calling/signaling.js";
import { cryptoEngine } from "../crypto/index.js";
import { api } from "../lib/api.js";
import { bs, fromB64, randomBytes, toB64, utf8 } from "../lib/bytes.js";
import { usePresenceStore } from "../state/presence.js";
import { channel } from "../transport/channel.js";
import {
  advanceOutgoingStatus,
  dequeueOutbox,
  deleteAttachmentBlob,
  enqueueOutbox,
  getAttachmentBlob,
  getContact,
  getMeta,
  insertAttachmentBlob,
  insertMessage,
  getMessage,
  deleteMessageLocally,
  markMessageDeleted,
  updateMessage,
  listContacts,
  listOutbox,
  markAttachmentViewed,
  patchAttachment,
  putMeta,
  markConversationRead,
  upsertContact,
  type AttachmentInfo,
  type Contact,
  type Reactions,
  type ReplyRef,
} from "../storage/db.js";

interface Account {
  userId: string;
  username: string;
  token: string;
}

let account: Account | null = null;

// "Online" means the app is on screen, not merely running: a backgrounded tab
// or a locked phone reports itself away.
function reportVisibility(): void {
  const visible = document.visibilityState === "visible";
  channel.setActive(visible);
  // Back on screen: reconnect at once if the phone dropped the connection
  // while locked, so a waiting call or message arrives immediately.
  if (visible) channel.resume();
}
function onNetworkBack(): void {
  channel.resume();
}
function reportHidden(): void {
  channel.setActive(false);
}
function trackVisibility(): void {
  reportVisibility();
  document.addEventListener("visibilitychange", reportVisibility);
  window.addEventListener("pagehide", reportHidden);
  window.addEventListener("pageshow", reportVisibility);
  window.addEventListener("online", onNetworkBack);
}
function untrackVisibility(): void {
  document.removeEventListener("visibilitychange", reportVisibility);
  window.removeEventListener("pagehide", reportHidden);
  window.removeEventListener("pageshow", reportVisibility);
  window.removeEventListener("online", onNetworkBack);
}

type ReceiptPayload =
  | { t: "delivered"; id: string }
  | { t: "viewed"; id: string }
  /** The recipient opened the chat and saw these messages (blue ticks). */
  | { t: "read"; ids: string[] };
type TextPayload = { t: "text"; id: string; text: string; sentAt: number; replyTo?: ReplyRef };
/** Changes to a message already sent — all end-to-end encrypted like the message itself. */
type EditPayload = { t: "edit"; id: string; text: string };
type DeletePayload = { t: "delete"; id: string };
type ReactPayload = { t: "react"; id: string; emoji: string | null };
type ControlPayload = EditPayload | DeletePayload | ReactPayload;
type AttachmentPayload = {
  t: "attachment";
  id: string;
  attachmentId: string;
  mimeType: string;
  filename: string;
  size: number;
  /** Set for a recorded voice message (replayable, with a duration). */
  voice?: true;
  durationMs?: number;
  key: string; // b64 raw AES-256 key, one-time, independent of the MLS conversation key
  iv: string; // b64
  sentAt: number;
};

/** Attachment bytes never touch the MLS conversation key — a fresh one-time
 *  AES-256-GCM key travels inside the (small) MLS-encrypted pointer message,
 *  while the (potentially large) ciphertext blob goes through the relay's
 *  separate attachment store, which never sees the key. */
async function encryptAttachment(bytes: Uint8Array): Promise<{ key: string; iv: string; ciphertext: Uint8Array }> {
  const rawKey = randomBytes(32);
  const iv = randomBytes(12);
  const cryptoKey = await crypto.subtle.importKey("raw", rawKey, "AES-GCM", false, ["encrypt"]);
  const ciphertext = bs(new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, cryptoKey, bs(bytes))));
  return { key: toB64(rawKey), iv: toB64(iv), ciphertext };
}

async function decryptAttachment(ciphertext: Uint8Array, keyB64: string, ivB64: string): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey("raw", fromB64(keyB64), "AES-GCM", false, ["decrypt"]);
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: fromB64(ivB64) }, cryptoKey, bs(ciphertext));
  return new Uint8Array(pt);
}

async function persistCrypto(): Promise<void> {
  await putMeta("cryptoSnapshot", await cryptoEngine.snapshot());
}

// The crypto engine holds one mutable MLS state per conversation and is not
// safe for concurrent access — e.g. several ICE candidates gathered in quick
// succession each trigger a `sendCallSignal`, and two overlapping
// encrypt-then-save calls can race and corrupt the secret-tree generation
// counter. Serialize every mutation (and its accompanying snapshot) through
// one queue so they always apply one at a time, in order.
let cryptoQueue: Promise<unknown> = Promise.resolve();
function withCrypto<T>(fn: () => Promise<T>): Promise<T> {
  const result = cryptoQueue.then(fn, fn);
  cryptoQueue = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

// MLS's per-sender generation counter only accepts messages in the order they
// were sent — decrypting a later message before an earlier one permanently
// rejects the earlier one ("Desired gen in the past"), since the ratchet has
// already moved past it. `withCrypto` alone doesn't guarantee that ordering:
// handleDeliver does an async Contact lookup *before* it ever reaches
// withCrypto, and that lookup's timing can vary per call, so two frames that
// arrive in order A-then-B could reach the crypto queue as B-then-A — exactly
// the pattern that shows up as a real call (an offer followed by several ICE
// candidates, arriving in quick succession) silently losing signaling frames.
// Queuing the *entire* handler, not just its crypto calls, makes processing
// order match arrival order regardless of each call's internal await timing.
// A handler can include a network fetch (attachments), so cap how long any one
// item can hold the queue — a download that never completes on a flaky mobile
// connection must not stall every message behind it forever.
const DELIVER_ITEM_TIMEOUT_MS = 60_000;
let deliverQueue: Promise<unknown> = Promise.resolve();
function enqueueDeliver(fn: () => Promise<void>): void {
  const run = () =>
    new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        console.warn("inbound envelope handler exceeded its time budget; continuing with the queue");
        resolve();
      }, DELIVER_ITEM_TIMEOUT_MS);
      fn().finally(() => {
        clearTimeout(timer);
        resolve();
      });
    });
  deliverQueue = deliverQueue.then(run, run);
}

function creds() {
  if (!account) throw new Error("messaging not started");
  return { userId: account.userId, token: account.token };
}

/**
 * Send an edit / delete / reaction for an earlier message. Not in the outbox:
 * the relay queues it for an offline recipient like any message, and the
 * channel holds it while we're offline ourselves.
 */
async function sendControl(peerUserId: string, payload: ControlPayload): Promise<void> {
  const contact = await getContact(peerUserId);
  if (!contact) throw new Error("Unknown contact.");
  const ciphertext = await withCrypto(async () => {
    const ct = await cryptoEngine.encrypt(contact.conversationId, utf8.encode(JSON.stringify(payload)));
    await persistCrypto();
    return ct;
  });
  channel.send({ t: "send", id: crypto.randomUUID(), to: peerUserId, kind: "message", ciphertext, ts: Date.now() });
}

function withReaction(current: Reactions | undefined, side: keyof Reactions, emoji: string | null): Reactions {
  const next: Reactions = { ...current };
  if (emoji) next[side] = emoji;
  else delete next[side];
  return next;
}

async function sendReceipt(conversationId: string, peerUserId: string, receipt: ReceiptPayload): Promise<void> {
  const ct = await withCrypto(async () => {
    const c = await cryptoEngine.encrypt(conversationId, utf8.encode(JSON.stringify(receipt)));
    await persistCrypto();
    return c;
  });
  channel.send({ t: "send", id: crypto.randomUUID(), to: peerUserId, kind: "receipt", ciphertext: ct, ts: Date.now() });
}

export const messaging = {
  get account(): Account | null {
    return account;
  },

  async start(acc: Account): Promise<void> {
    account = acc;
    channel.connect(
      { userId: acc.userId, token: acc.token },
      {
        onStatus: (s) => statusListeners.forEach((l) => l(s)),
        onDeliver: (f) => enqueueDeliver(() => handleDeliver(f)),
        onSent: (id, live, pushable) => {
          const resolveCallSignal = pendingCallSignalAcks.get(id);
          if (resolveCallSignal) {
            pendingCallSignalAcks.delete(id);
            resolveCallSignal({ live, pushable });
          } else {
            void advanceOutgoingStatus(id, "sent");
          }
        },
        onDrained: () => void flushOutbox(),
        onError: (e) => console.warn("relay error", e.code, e.message),
        onPresence: (f) => usePresenceStore.getState().set(f.userId, { online: f.online, lastSeen: f.lastSeen }),
      },
    );
    trackVisibility();
    void repairContactNames();
    await flushOutbox();
  },

  stop(): void {
    untrackVisibility();
    channel.disconnect();
    usePresenceStore.getState().clear();
    account = null;
  },

  /** Follow these contacts' online status. Safe to call repeatedly. */
  watchPresence(userIds: string[]): void {
    channel.watch(userIds);
  },

  /**
   * Mark a conversation's unread messages as read and tell the sender, so their
   * ticks turn blue. Call only while the chat is actually on screen.
   */
  async markRead(peerUserId: string): Promise<void> {
    const ids = await markConversationRead(peerUserId);
    if (ids.length === 0) return;
    const contact = await getContact(peerUserId);
    if (contact) await sendReceipt(contact.conversationId, peerUserId, { t: "read", ids });
  },

  async createInvite(): Promise<{ url: string; token: string; expiresAt: number }> {
    return api.createInvite(creds());
  },

  /** Resolve an invite token, build the conversation, add the contact. */
  async addContactByInvite(token: string): Promise<Contact> {
    const bundle = await api.resolveInvite(token);
    if (bundle.userId === account?.userId) throw new Error("That's your own invite link.");

    let conversationId = cryptoEngine.conversationFor(bundle.userId);
    if (!conversationId) {
      const created = await withCrypto(async () => {
        const c = await cryptoEngine.createConversation({
          userId: bundle.userId,
          signaturePublicKey: bundle.signaturePublicKey,
          keyPackage: bundle.keyPackage,
        });
        await persistCrypto();
        return c;
      });
      conversationId = created.conversationId;
      // Send the Welcome so the peer can join.
      channel.send({
        t: "send",
        id: crypto.randomUUID(),
        to: bundle.userId,
        kind: "group-welcome",
        ciphertext: created.welcome,
        ts: Date.now(),
      });
    }

    const contact: Contact = {
      peerUserId: bundle.userId,
      username: bundle.username,
      conversationId,
      signaturePublicKey: bundle.signaturePublicKey,
      verified: false,
      addedAt: Date.now(),
    };
    await upsertContact(contact);
    return contact;
  },

  async sendText(peerUserId: string, text: string, replyTo?: ReplyRef): Promise<void> {
    const contact = await getContact(peerUserId);
    if (!contact) throw new Error("Unknown contact.");
    const id = crypto.randomUUID();
    const now = Date.now();

    await insertMessage({
      id,
      peerUserId,
      dir: "out",
      kind: "text",
      text,
      createdAt: now,
      deliveredAt: null,
      expiresAt: null,
      status: "queued",
      read: true,
      ...(replyTo ? { replyTo } : {}),
    });

    const payload: TextPayload = { t: "text", id, text, sentAt: now, ...(replyTo ? { replyTo } : {}) };
    const ciphertext = await withCrypto(async () => {
      const ct = await cryptoEngine.encrypt(contact.conversationId, utf8.encode(JSON.stringify(payload)));
      await persistCrypto();
      return ct;
    });

    await enqueueOutbox({ id, peerUserId, kind: "message", ciphertext, createdAt: now, tries: 0 });
    channel.send({ t: "send", id, to: peerUserId, kind: "message", ciphertext, ts: now, notify: "message" });
  },

  /**
   * Encrypts `file` with a one-time key, uploads the ciphertext to the
   * relay's separate attachment store (too big for the normal 256KB message
   * cap), then sends a small pointer message carrying that key. View-once:
   * the sender keeps its own copy locally until the recipient actually opens
   * it — see `viewAttachment` and the "viewed" receipt handling below.
   */
  async sendAttachment(
    peerUserId: string,
    file: File,
    meta?: { voice?: true; durationMs?: number },
  ): Promise<void> {
    const contact = await getContact(peerUserId);
    if (!contact) throw new Error("Unknown contact.");
    const id = crypto.randomUUID();
    const now = Date.now();
    const bytes = bs(new Uint8Array(await file.arrayBuffer()));

    const attachment: AttachmentInfo = {
      mimeType: file.type,
      filename: file.name,
      size: bytes.length,
      viewed: false,
      ...(meta?.voice ? { voice: true } : {}),
      ...(meta?.durationMs !== undefined ? { durationMs: meta.durationMs } : {}),
    };
    await insertMessage({
      id,
      peerUserId,
      dir: "out",
      kind: "attachment",
      text: "",
      attachment,
      createdAt: now,
      deliveredAt: null,
      expiresAt: null,
      status: "queued",
      read: true,
    });
    await insertAttachmentBlob(id, bytes);

    const { key, iv, ciphertext } = await encryptAttachment(bytes);
    const { attachmentId } = await api.uploadAttachment(creds(), peerUserId, file.type, ciphertext);

    const payload: AttachmentPayload = {
      t: "attachment",
      id,
      attachmentId,
      mimeType: file.type,
      filename: file.name,
      size: bytes.length,
      key,
      iv,
      sentAt: now,
      ...(meta?.voice ? { voice: true as const } : {}),
      ...(meta?.durationMs !== undefined ? { durationMs: meta.durationMs } : {}),
    };
    const pointerCiphertext = await withCrypto(async () => {
      const ct = await cryptoEngine.encrypt(contact.conversationId, utf8.encode(JSON.stringify(payload)));
      await persistCrypto();
      return ct;
    });

    await enqueueOutbox({ id, peerUserId, kind: "message", ciphertext: pointerCiphertext, createdAt: now, tries: 0 });
    channel.send({
      t: "send",
      id,
      to: peerUserId,
      kind: "message",
      ciphertext: pointerCiphertext,
      ts: now,
      notify: "message",
    });
  },

  /**
   * Called when the user taps to open a received attachment. Returns the
   * bytes for the UI to display, then immediately deletes the local copy and
   * tells the sender to delete theirs too — view-once, both sides.
   */
  /** Change the text of a message you sent. */
  async editMessage(peerUserId: string, messageId: string, text: string): Promise<void> {
    const m = await getMessage(messageId);
    if (!m || m.dir !== "out" || m.kind !== "text" || m.deleted || m.peerUserId !== peerUserId) return;
    if (text === m.text) return;
    await updateMessage(messageId, { text, edited: true });
    await sendControl(peerUserId, { t: "edit", id: messageId, text });
  },

  /** Remove a message you sent from both phones, leaving "This message was deleted". */
  async deleteForEveryone(peerUserId: string, messageId: string): Promise<void> {
    const m = await getMessage(messageId);
    if (!m || m.dir !== "out" || m.peerUserId !== peerUserId) return;
    await markMessageDeleted(messageId);
    // Not yet delivered? Don't let a later outbox resend deliver it after all.
    await dequeueOutbox(messageId);
    await sendControl(peerUserId, { t: "delete", id: messageId });
  },

  /** Remove a message from this phone only. */
  async deleteForMe(messageId: string): Promise<void> {
    await deleteMessageLocally(messageId);
  },

  /** React with an emoji, or `null` to take your reaction back. */
  async react(peerUserId: string, messageId: string, emoji: string | null): Promise<void> {
    const m = await getMessage(messageId);
    if (!m || m.deleted || m.peerUserId !== peerUserId) return;
    await updateMessage(messageId, { reactions: withReaction(m.reactions, "mine", emoji) });
    await sendControl(peerUserId, { t: "react", id: messageId, emoji });
  },

  async viewAttachment(messageId: string, peerUserId: string): Promise<Uint8Array> {
    const bytes = await getAttachmentBlob(messageId);
    if (!bytes) throw new Error("Attachment already gone.");
    await deleteAttachmentBlob(messageId);
    await markAttachmentViewed(messageId);

    const contact = await getContact(peerUserId);
    if (contact) await sendReceipt(contact.conversationId, peerUserId, { t: "viewed", id: messageId });
    return bytes;
  },

  /**
   * The recipient played a voice note. Reuses the existing "viewed" receipt —
   * the sender drops their copy — but unlike a photo, this side keeps the
   * clip so it can be replayed until the message self-destructs.
   */
  async markVoiceHeard(messageId: string, peerUserId: string): Promise<void> {
    const m = await getMessage(messageId);
    if (!m?.attachment?.voice || m.attachment.heard) return;
    await patchAttachment(messageId, { heard: true });
    if (m.dir !== "in") return;
    const contact = await getContact(peerUserId);
    if (contact) await sendReceipt(contact.conversationId, peerUserId, { t: "viewed", id: messageId });
  },

  onStatus(fn: (s: string) => void): () => void {
    statusListeners.push(fn);
    return () => {
      statusListeners = statusListeners.filter((l) => l !== fn);
    };
  },

  /**
   * Send a call-signaling envelope, encrypted the same way as a chat message.
   * Never persisted to the outbox — the relay holds `call-signal` only for as
   * long as a call rings, so a retry later would be pointless. Resolves with
   * whether the peer was connected right now (`live`) and whether the relay
   * can wake them with a push notification (`pushable`, only asked for when
   * `notify` is set); both `false` if no `sent` frame arrives within the
   * timeout (e.g. we're offline).
   */
  async sendCallSignal(
    peerUserId: string,
    payload: CallSignalPayload,
    notify?: "call",
  ): Promise<CallSignalResult> {
    const contact = await getContact(peerUserId);
    if (!contact) throw new Error("Unknown contact.");
    const ciphertext = await withCrypto(async () => {
      const ct = await cryptoEngine.encrypt(contact.conversationId, utf8.encode(JSON.stringify(payload)));
      await persistCrypto();
      return ct;
    });

    const id = crypto.randomUUID();
    const result = new Promise<CallSignalResult>((resolve) => {
      pendingCallSignalAcks.set(id, resolve);
      setTimeout(() => {
        if (pendingCallSignalAcks.delete(id)) resolve({ live: false, pushable: false });
      }, 5000);
    });
    channel.send({ t: "send", id, to: peerUserId, kind: "call-signal", ciphertext, ts: Date.now(), notify });
    return result;
  },

  /** Fires for every newly received chat message or attachment. */
  onIncomingMessage(fn: (peerUserId: string) => void): () => void {
    incomingMessageListeners.push(fn);
    return () => {
      incomingMessageListeners = incomingMessageListeners.filter((l) => l !== fn);
    };
  },

  onCallSignal(fn: (from: string, payload: CallSignalPayload) => void): () => void {
    callSignalListeners.push(fn);
    return () => {
      callSignalListeners = callSignalListeners.filter((l) => l !== fn);
    };
  },
};

let statusListeners: Array<(s: string) => void> = [];
let callSignalListeners: Array<(from: string, payload: CallSignalPayload) => void> = [];
let incomingMessageListeners: Array<(peerUserId: string) => void> = [];
export interface CallSignalResult {
  live: boolean;
  pushable: boolean;
}
const pendingCallSignalAcks = new Map<string, (r: CallSignalResult) => void>();

async function flushOutbox(): Promise<void> {
  if (!channel.connected) return;
  for (const item of await listOutbox()) {
    channel.send({
      t: "send",
      id: item.id,
      to: item.peerUserId,
      kind: item.kind as EnvelopeKind,
      ciphertext: item.ciphertext,
      ts: item.createdAt,
      // The relay only notifies the first time it sees an envelope id, so a
      // resend never double-notifies — but if the original send never made it
      // out (we were offline), this is the one that should.
      notify: item.kind === "message" ? "message" : undefined,
    });
  }
}

/** A contact created from an id alone shows this until its real name is known. */
function placeholderName(peerUserId: string): string {
  return peerUserId.slice(0, 8);
}

/**
 * Someone who added *us* joins through an MLS Welcome, which carries only
 * their user id — so they used to show up as "9ec9d6d8" forever. Ask the relay
 * for the username behind the id.
 */
async function resolveContactName(contact: Contact): Promise<void> {
  if (!account || contact.username !== placeholderName(contact.peerUserId)) return;
  try {
    const { username } = await api.lookupAccount(creds(), contact.peerUserId);
    const latest = await getContact(contact.peerUserId);
    if (latest && latest.username === placeholderName(contact.peerUserId)) {
      await upsertContact({ ...latest, username });
    }
  } catch (err) {
    console.warn("couldn't look up a contact's username", err);
  }
}

async function repairContactNames(): Promise<void> {
  try {
    for (const c of await listContacts()) await resolveContactName(c);
  } catch {
    /* retried on the next start */
  }
}

// The relay can hand over the same envelope twice — e.g. it went out over a
// socket iOS had quietly suspended, and again from the queue once the app
// reconnected. MLS can only decrypt a message once, so a second attempt fails
// and (never being ACKed) would come back on every reconnect. Remember what
// was already handled this session and just ACK repeats.
const handledIds = new Set<string>();
function ackHandled(id: string): void {
  handledIds.add(id);
  if (handledIds.size > 1000) handledIds.delete(handledIds.values().next().value!);
  channel.ack(id);
}

async function handleDeliver(f: DeliverFrame): Promise<void> {
  if (handledIds.has(f.id)) {
    channel.ack(f.id);
    return;
  }
  try {
    if (f.kind === "group-welcome") {
      const joined = await withCrypto(async () => {
        const j = await cryptoEngine.joinConversation(f.ciphertext, f.from);
        await persistCrypto();
        return j;
      });
      if (!(await getContact(joined.peerUserId))) {
        const contact: Contact = {
          peerUserId: joined.peerUserId,
          username: placeholderName(joined.peerUserId),
          conversationId: joined.conversationId,
          signaturePublicKey: "",
          verified: false,
          addedAt: Date.now(),
        };
        await upsertContact(contact);
        void resolveContactName(contact);
      }
      ackHandled(f.id);
      return;
    }

    let contact = await getContact(f.from);
    const conversationId = contact?.conversationId ?? cryptoEngine.conversationFor(f.from);
    if (!conversationId) {
      console.warn("deliver for unknown conversation", f.from);
      ackHandled(f.id); // drop — nothing we can do
      return;
    }
    // A known conversation with no Contact row means the contact was deleted
    // locally but the peer (who was never blocked — there's no such thing
    // here) messaged again. Re-surface them rather than silently dropping
    // otherwise-valid, decryptable messages into nowhere.
    if (!contact) {
      contact = { peerUserId: f.from, username: placeholderName(f.from), conversationId, signaturePublicKey: "", verified: false, addedAt: Date.now() };
      await upsertContact(contact);
      void resolveContactName(contact);
    }

    if (f.kind === "call-signal") {
      let payload: CallSignalPayload;
      try {
        const plaintext = await withCrypto(async () => {
          const pt = await cryptoEngine.decrypt(conversationId, f.ciphertext);
          await persistCrypto();
          return pt;
        });
        payload = JSON.parse(utf8.decode(plaintext)) as CallSignalPayload;
      } catch (err) {
        // Unlike a chat message, a call signal that can't be read now is
        // worthless later — drop it rather than have it redelivered.
        console.warn("dropping an unreadable call signal", err);
        ackHandled(f.id);
        return;
      }
      callSignalListeners.forEach((l) => l(f.from, payload));
      ackHandled(f.id);
      return;
    }

    const plaintext = await withCrypto(async () => {
      const pt = await cryptoEngine.decrypt(conversationId, f.ciphertext);
      await persistCrypto();
      return pt;
    });
    const msg = JSON.parse(utf8.decode(plaintext)) as TextPayload | AttachmentPayload | ReceiptPayload | ControlPayload;

    if (msg.t === "text") {
      const now = Date.now();
      await insertMessage({
        id: msg.id,
        peerUserId: f.from,
        dir: "in",
        kind: "text",
        text: msg.text,
        createdAt: msg.sentAt,
        deliveredAt: now,
        expiresAt: now + DELIVERED_TTL_MS,
        status: "delivered",
        read: false,
        ...(msg.replyTo ? { replyTo: msg.replyTo } : {}),
      });
      ackHandled(f.id);
      incomingMessageListeners.forEach((l) => l(f.from));
      await sendReceipt(conversationId, f.from, { t: "delivered", id: msg.id });
    } else if (msg.t === "edit" || msg.t === "delete" || msg.t === "react") {
      // Only the person who sent a message may edit or delete it; either side
      // may react. Anything aimed at a message that has already self-destructed
      // (or was never here) is simply dropped.
      const m = await getMessage(msg.id);
      if (m && m.peerUserId === f.from && !m.deleted) {
        if (msg.t === "edit" && m.dir === "in" && m.kind === "text") {
          await updateMessage(msg.id, { text: msg.text, edited: true });
        } else if (msg.t === "delete" && m.dir === "in") {
          await markMessageDeleted(msg.id);
        } else if (msg.t === "react") {
          await updateMessage(msg.id, { reactions: withReaction(m.reactions, "theirs", msg.emoji) });
        }
      }
      ackHandled(f.id);
    } else if (msg.t === "attachment") {
      const now = Date.now();
      const ciphertext = await api.fetchAttachment(creds(), msg.attachmentId);
      const bytes = await decryptAttachment(ciphertext, msg.key, msg.iv);
      const attachment: AttachmentInfo = {
        mimeType: msg.mimeType,
        filename: msg.filename,
        size: msg.size,
        viewed: false,
        ...(msg.voice ? { voice: true } : {}),
        ...(msg.durationMs !== undefined ? { durationMs: msg.durationMs } : {}),
      };
      await insertMessage({
        id: msg.id,
        peerUserId: f.from,
        dir: "in",
        kind: "attachment",
        text: "",
        attachment,
        createdAt: msg.sentAt,
        deliveredAt: now,
        expiresAt: now + DELIVERED_TTL_MS,
        status: "delivered",
        read: false,
      });
      await insertAttachmentBlob(msg.id, bytes);
      ackHandled(f.id);
      incomingMessageListeners.forEach((l) => l(f.from));
      await sendReceipt(conversationId, f.from, { t: "delivered", id: msg.id });
    } else if (msg.t === "delivered") {
      await advanceOutgoingStatus(msg.id, "delivered");
      await dequeueOutbox(msg.id);
      ackHandled(f.id);
    } else if (msg.t === "read") {
      for (const id of msg.ids) {
        await advanceOutgoingStatus(id, "read");
        // Read implies delivered, even if that receipt went missing.
        await dequeueOutbox(id);
      }
      ackHandled(f.id);
    } else if (msg.t === "viewed") {
      // The recipient opened an attachment we sent — view-once means our own
      // copy goes too.
      await deleteAttachmentBlob(msg.id);
      await markAttachmentViewed(msg.id);
      ackHandled(f.id);
    }
  } catch (err) {
    console.error("failed to handle inbound envelope", err);
    // Do NOT ack — let the relay redeliver within the grace window.
  }
}

/** Boot helper used by the session store after unlock. */
export async function loadAccount(): Promise<Account | null> {
  return (await getMeta<Account>("account")) ?? null;
}

export type { Account };
